/** Состояние сервера: видно ли беду до того, как что-то отвалится. */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, formatSize } from '../api'
import type { ServerStatus } from '../api'
import { Accounts } from '../components/Accounts'
import { Spinner } from '../components/Spinner'

export function StatusPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState<ServerStatus | null>(null)
  const [error, setError] = useState('')
  const [scanning, setScanning] = useState(false)

  const load = () => {
    api
      .status()
      .then(setStatus)
      .catch(() => setError('не удалось получить состояние'))
  }

  useEffect(load, [])

  const rescan = async () => {
    setScanning(true)
    try {
      await api.rescan()
      // Сканирование идёт в фоне: обновляем сведения чуть позже.
      window.setTimeout(load, 2000)
    } finally {
      setScanning(false)
    }
  }

  if (error) return <p className="error">{error}</p>
  if (!status) return <Spinner />

  const health = status.health

  return (
    <>
      <div className="topbar">
        <button className="btn btn--ghost" onClick={() => navigate('/')}>
          ← Каталог
        </button>
        <span className="topbar__brand">Состояние</span>
        <span className="topbar__spacer" />
        <button className="btn" onClick={load}>
          Обновить
        </button>
      </div>

      <div className="content status">
        {health.warnings.length > 0 && (
          <div className="status__warnings">
            {health.warnings.map((text) => (
              <div key={text} className="status__warning">
                ⚠ {text}
              </div>
            ))}
          </div>
        )}

        <section>
          <h3>Библиотека</h3>
          <dl className="status__grid">
            <dt>Книг</dt>
            <dd>{status.books.toLocaleString('ru-RU')}</dd>
            <dt>Файлов</dt>
            <dd>{status.files.toLocaleString('ru-RU')}</dd>
            <dt>Авторов</dt>
            <dd>{status.authors.toLocaleString('ru-RU')}</dd>
            <dt>Серий</dt>
            <dd>{status.series.toLocaleString('ru-RU')}</dd>
            <dt>Размер базы</dt>
            <dd>{formatSize(health.database_bytes)}</dd>
          </dl>
          <button className="btn" disabled={status.scanning || scanning} onClick={() => void rescan()}>
            {status.scanning ? 'Сканирование идёт…' : 'Пересканировать'}
          </button>
        </section>

        <section>
          <h3>Папки с книгами</h3>
          {health.roots.map((root) => (
            <div key={root.path}>
              {root.available ? '✓' : '✗'} {root.label} — <span className="dim">{root.path}</span>
              {!root.available && <span className="error"> недоступна</span>}
            </div>
          ))}
        </section>

        <section>
          <h3>Диски</h3>
          {health.disks.map((disk) => (
            <div key={disk.drive}>
              {disk.drive} свободно {formatSize(disk.free_bytes)} из{' '}
              {formatSize(disk.total_bytes)}{' '}
              <span className={disk.free_ratio < 0.05 ? 'error' : 'dim'}>
                ({Math.round(disk.free_ratio * 100)} %)
              </span>
            </div>
          ))}
        </section>

        <section>
          <h3>Резервные копии</h3>
          {health.backup.count === 0 ? (
            <p className="error">Копий нет.</p>
          ) : (
            <div>
              Копий: {health.backup.count}, последняя{' '}
              {new Date(health.backup.latest_at * 1000).toLocaleString('ru-RU')} (
              {formatSize(health.backup.bytes)})
            </div>
          )}
        </section>

        <Accounts />

        <section>
          <h3>Служба</h3>
          <div>Работает без перезапуска: {formatUptime(health.uptime_seconds)}</div>
          <div className="dim">Ревизия синхронизации: {status.sync_rev}</div>
        </section>
      </div>
    </>
  )
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (days > 0) return `${days} д ${hours} ч`
  if (hours > 0) return `${hours} ч ${minutes} мин`
  return `${minutes} мин`
}
